/**
 * SetupTakeover — replaces the Overview tab body while a sandbox is being
 * provisioned. Five-step progression card with a pulsing canary accent.
 *
 * "Run health checks" (step 4) is active once the instance is `running` but
 * before the first health-check result has landed; after that the page hands
 * off to the Overview tab, which surfaces the actual probe results.
 */
import React from 'react';
import { Card, Heading, Text } from '@chatsdet/ui';
import { Cube } from '@phosphor-icons/react';
import type { EnvironmentInstanceStatus } from '../../../hooks/useEnvironmentInstances';
import { SandboxSetupChecklist } from './SandboxSetupChecklist';

export interface SetupTakeoverProps {
  status: EnvironmentInstanceStatus;
}

export function SetupTakeover({ status }: SetupTakeoverProps) {
  return (
    <div className="flex flex-col gap-4 max-w-[640px] mx-auto">
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-border bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-md bg-primary">
              <Cube size={18} weight="fill" className="text-primary-foreground" />
              <span
                aria-hidden
                className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-zinc-900 animate-pulse"
              />
            </div>
            <div>
              <Heading level={4} className="text-[18px]">
                Setting up your sandbox
              </Heading>
              <Text size="sm" color="muted">
                This usually takes 2–3 minutes.
              </Text>
            </div>
          </div>
        </div>
        <SandboxSetupChecklist status={status} className="p-5" />
      </Card>
      <Card padding="sm">
        <Text size="sm" color="muted" className="text-center">
          Other tabs become available once the sandbox is running.
        </Text>
      </Card>
    </div>
  );
}

SetupTakeover.displayName = 'SetupTakeover';
